import React, { useRef, useState, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Box, useTheme } from '@mui/material'
import ChartGrid from './ChartGrid'
import ChartAxis from './ChartAxis'
import ChartPath from './ChartPath'
import ChartTooltip from './ChartTooltip'
import { PADDING, project } from '../utils/constants'

const GraphicChart = ({ dataPoints, min, max, format, t_ }) => {
  const theme = useTheme()
  const containerRef = useRef(null)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const [tooltip, setTooltip] = useState(null)

  useEffect(() => {
    if (!containerRef.current) return
    const resizeObserver = new window.ResizeObserver((entries) => {
      for (const entry of entries) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height
        })
      }
    })
    resizeObserver.observe(containerRef.current)
    return () => resizeObserver.disconnect()
  }, [])

  const { width, height } = dimensions
  const lineColor = theme.palette.primary.main
  const gridColor = theme.palette.divider
  const textColor = theme.palette.text.secondary

  const bounds = useMemo(() => {
    if (dataPoints.length === 0) return { minY: min, maxY: max }
    const ys = dataPoints.map((d) => d.y)
    const minY = Math.min(min, ...ys)
    const maxY = Math.max(max, ...ys)
    return { minY, maxY }
  }, [dataPoints, min, max])

  const handleMouseMove = (evt) => {
    if (!width || !height || dataPoints.length === 0) return
    const rect = containerRef.current.getBoundingClientRect()
    const mouseX = evt.clientX - rect.left

    const graphW = width - PADDING.l - PADDING.r
    const relativeX = mouseX - PADDING.l

    const pointsCount = Math.max(1, dataPoints.length - 1)
    let idx = Math.round((relativeX / graphW) * pointsCount)
    idx = Math.max(0, Math.min(dataPoints.length - 1, idx))

    const point = dataPoints[idx]
    if (!point) return

    const { x, y } = project(width, height, idx, point.y, bounds, dataPoints.length)

    setTooltip((prev) => {
      if (prev && prev.idx === idx) return prev
      let dateLabel = ''
      try {
        if (point.ts) {
          const d = new Date(point.ts)
          const now = new Date()
          const sameYear = d.getFullYear() === now.getFullYear()
          dateLabel = d.toLocaleDateString('en-GB', sameYear ? { day: '2-digit', month: 'short' } : { day: '2-digit', month: 'short', year: 'numeric' })
        }
      } catch (e) {
        dateLabel = ''
      }
      return {
        idx,
        x,
        y,
        label: point.label,
        dateLabel,
        value: typeof point.y === 'number' ? point.y.toFixed(2) : point.y
      }
    })
  }

  const clearTooltip = () => setTooltip(null)

  if (width === 0 || height === 0) {
    return <Box ref={containerRef} sx={{ flex: 1, position: 'relative', minHeight: 0 }} />
  }

  return (
    <Box ref={containerRef} sx={{ flex: 1, position: 'relative', minHeight: 0 }}>
      <svg
        style={{ width: '100%', height: '100%', overflow: 'hidden', cursor: 'crosshair', display: 'block' }}
        viewBox={`0 0 ${width} ${height}`}
        onMouseMove={handleMouseMove}
        onMouseLeave={clearTooltip}
      >
        <defs>
          <filter id='glow' x='-50%' y='-50%' width='200%' height='200%'>
            <feGaussianBlur stdDeviation='3' result='coloredBlur' />
            <feMerge>
              <feMergeNode in='coloredBlur' />
              <feMergeNode in='SourceGraphic' />
            </feMerge>
          </filter>
        </defs>

        <ChartGrid gridColor={gridColor} width={width} height={height} />
        <ChartAxis bounds={bounds} dataPoints={dataPoints} textColor={textColor} format={format} t_={t_} width={width} height={height} />

        {dataPoints.length > 0 && (
          <ChartPath
            dataPoints={dataPoints}
            bounds={bounds}
            lineColor={lineColor}
            animated
            width={width}
            height={height}
          />
        )}

        {/* Hover Cursor Line */}
        {tooltip && (
          <line
            x1={tooltip.x}
            y1={PADDING.t}
            x2={tooltip.x}
            y2={height - PADDING.b}
            stroke={lineColor}
            strokeWidth={1.5}
            strokeDasharray='4,4'
            opacity={0.8}
          />
        )}

        {/* Hover Point Indicator */}
        {tooltip && (
          <circle
            cx={tooltip.x}
            cy={tooltip.y}
            r={6}
            fill={theme.palette.background.paper}
            stroke={lineColor}
            strokeWidth={3}
            style={{ filter: 'url(#glow)' }}
          />
        )}
      </svg>

      <ChartTooltip tooltip={tooltip} lineColor={lineColor} theme={theme} width={width} height={height} />
    </Box>
  )
}

GraphicChart.propTypes = {
  dataPoints: PropTypes.array.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  format: PropTypes.string,
  t_: PropTypes.func
}

export default GraphicChart
